import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, AppBar, Toolbar, IconButton, Container } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, updateDoc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import CustomToast from '../components/CustomToast';

function NicknameEditPage() {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const inputRef = useRef();

  useEffect(() => {
    // 현재 닉네임 불러오기 및 포커스
    const user = auth.currentUser;
    if (user) setNickname(user.displayName || '');
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 100);
  }, []);

  const validateNickname = (nickname) => {
    if (!nickname) return '닉네임을 입력해주세요.';
    if (nickname.length > 12) return '닉네임은 12자 이내로 입력해주세요.';
    if (!/^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9\s]+$/.test(nickname)) {
      return '한글(자/모음 가능), 영문, 숫자, 띄어쓰기만 사용 가능합니다.';
    }
    return '';
  };

  const handleChange = (e) => {
    setNickname(e.target.value);
    setError(validateNickname(e.target.value));
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    const err = validateNickname(nickname);
    if (err) {
      setError(err);
      return;
    }
    if (!user) return;
    setLoading(true);
    try {
      await updateProfile(user, { displayName: nickname });
      const userRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userRef);
      if (userDocSnap.exists()) {
        await updateDoc(userRef, { displayName: nickname, updatedAt: serverTimestamp() });
      } else {
        await setDoc(userRef, {
          displayName: nickname,
          email: user.email,
          photoURL: user.photoURL,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setTimeout(() => navigate('/mypage'), 500);
    } catch (e) {
      setSnackbar({ open: true, message: '닉네임 수정 중 오류가 발생했습니다.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff' }}>
      <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: '1px solid #eee', bgcolor: '#fff' }}>
        <Toolbar sx={{ minHeight: 56, px: 2 }}>
          <IconButton edge="start" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700, ml: 1 }}>닉네임 수정</Typography>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!!error || !nickname || loading}
            sx={{
              bgcolor: '#111', color: '#fff', borderRadius: 2, px: 3, boxShadow: 'none',
              '&:hover': { bgcolor: '#222' },
              fontWeight: 600, fontSize: '1rem', ml: 2
            }}
          >
            저장
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ mt: 2, px: 2 }}>
        {/* 제목 섹션 */}
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              color: '#111',
              fontFamily: 'NanumSquareEB',
              letterSpacing: '-0.02em',
              mb: 1
            }}
          >
            닉네임
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              fontFamily: 'NanumSquareR'
            }}
          >
            약해보이지 않는 닉네임을 설정해주세요
          </Typography>
        </Box>

        {/* 입력 영역 */}
        <Box sx={{ position: 'relative', mb: 4 }}>
          <TextField
            value={nickname}
            onChange={handleChange}
            error={!!error}
            variant="outlined"
            fullWidth
            inputRef={inputRef}
            placeholder="닉네임을 입력하세요..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                backgroundColor: '#fafbfc',
                border: '1px solid',
                borderColor: error ? '#ff6b6b' : '#e8eaed',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: error ? '#ff6b6b' : '#d0d7de',
                  backgroundColor: '#f8f9fa',
                },
                '&.Mui-focused': {
                  borderColor: error ? '#ff6b6b' : '#111',
                  backgroundColor: '#fff',
                  boxShadow: error 
                    ? '0 0 0 3px rgba(255, 107, 107, 0.1)' 
                    : '0 0 0 3px rgba(17, 17, 17, 0.08)',
                },
                '&.Mui-error': {
                  borderColor: '#ff6b6b',
                  '&:hover': {
                    borderColor: '#ff5252',
                  },
                  '&.Mui-focused': {
                    borderColor: '#ff6b6b',
                    boxShadow: '0 0 0 3px rgba(255, 107, 107, 0.1)',
                  },
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '16px 16px 16px 16px',
                fontSize: '1.1rem',
                lineHeight: 1.5,
                color: '#111',
                fontWeight: 500,
                fontFamily: 'NanumSquareB',
                letterSpacing: '-0.01em',
                '&::placeholder': {
                  color: '#999',
                  opacity: 1,
                  fontWeight: 400,
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{ 
              inputProps: { maxLength: 12 }
            }}
          />
          
          {/* 글자 수 카운터 */}
          <Box sx={{
            position: 'absolute',
            bottom: 12,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: nickname.length > 10 ? (nickname.length > 12 ? '#ff6b6b' : '#ffa726') : '#999',
                fontWeight: 500,
                fontFamily: 'NanumSquareB',
                transition: 'color 0.2s ease',
              }}
            >
              {nickname.length}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#ccc',
                fontWeight: 400,
              }}
            >
              / 12
            </Typography>
          </Box>
        </Box>

        {/* 에러 메시지 */}
        {error && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            borderRadius: '12px', 
            backgroundColor: '#fff5f5',
            border: '1px solid #fed7d7',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Typography sx={{ 
              color: '#e53e3e', 
              fontSize: '0.875rem',
              fontWeight: 500,
              fontFamily: 'NanumSquareB'
            }}>
              {error}
            </Typography>
          </Box>
        )}
      </Container>
      <CustomToast 
        open={snackbar.open} 
        message={snackbar.message} 
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}

export default NicknameEditPage; 